import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { withAlert } from "react-alert";
import { Badge, Button, Form, Modal, Nav, Table } from "react-bootstrap";
import { components } from "react-select";
import CreatableSelect from "react-select/creatable";
import Swal from "sweetalert2";
import "sweetalert2/src/sweetalert2.scss";
import Footer from "../../common/Footer.js";
import AdminSideNav from "../../common/AdminSideNav.js";
import TopNav from "../../common/TopNav.js";
import requireAuth from "../../hoc/requireAuth";
import authorizeAdmin from "../../hoc/authorizeAdmin";
import { refreshAclCodes } from "../../store/actions/authActions";

const PLATFORMS = [
  { value: "domain", label: "Domain" },
  { value: "website", label: "Website" },
  { value: "social-media", label: "Social Media" },
  { value: "e-commerce", label: "E-Commerce" },
  { value: "app-store", label: "App Store" },
  { value: "tmch", label: "TMCH" },
];

const MAIN_PLATFORM = "E-Commerce";

const TABS = {
  status: { title: "Status", aclType: "StatusCode" },
  reason: { title: "Reason", aclType: "ReasonCode" },
};

const DEFAULT_GROUP = "Default";

const isDefaultGroup = (group) => group.toLowerCase() === DEFAULT_GROUP.toLowerCase();

const emptyForm = () => ({ group: DEFAULT_GROUP, value: "", label: "", permissions: [], isActive: true });

// "books store" -> "BooksStore"
const groupName = (input) => {
  const words = input.split(/[^A-Za-z0-9]+/).filter(Boolean);
  if (!words.length || !/^[A-Za-z]/.test(words[0])) return "";

  return words.map((word) => word[0].toUpperCase() + word.slice(1)).join("");
};

const errorText = (error) => {
  const data = error.response?.data;
  const first = data?.errors?.[0];
  return (first && Object.values(first)[0]) || data?.message || "Something went wrong.";
};

const platformOrder = (platform) => PLATFORMS.findIndex((item) => item.value === platform);

const tabOf = (aclType) => Object.keys(TABS).find((key) => TABS[key].aclType === aclType);

function GroupOption(props) {
  const { data, selectProps, children } = props;

  return (
    <components.Option {...props}>
      <div className="d-flex justify-content-between align-items-center">
        <span>{children}</span>
        {!data.__isNew__ && !isDefaultGroup(data.value) && (
          <span
            role="button"
            aria-label={`Delete group ${data.value}`}
            title="Delete group"
            className="text-danger px-2"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              selectProps.onDeleteGroup(data.value);
            }}
          >
            &times;
          </span>
        )}
      </div>
    </components.Option>
  );
}

function GroupMenuList(props) {
  return (
    <components.MenuList {...props}>
      {!props.selectProps.inputValue && (
        <div role="button" className="px-3 py-2 text-primary border-bottom" style={{ cursor: "pointer" }} onClick={props.selectProps.onNewGroup}>
          + Create new group
        </div>
      )}
      {props.children}
    </components.MenuList>
  );
}

function ManageAcl({ alert, dispatch }) {
  const [tab, setTab] = useState("status");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newGroup, setNewGroup] = useState(null);

  useEffect(() => {
    window.axios
      .get("/acl")
      .then((response) => setRows(response.data))
      .catch((error) => alert.show(errorText(error), { type: "error" }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countFor = (key) => rows.filter((row) => tabOf(row.aclType) === key).length;

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows
      .filter((row) => tabOf(row.aclType) === tab)
      .filter((row) => !term || row.value.toLowerCase().includes(term) || row.label.toLowerCase().includes(term) || row.group.toLowerCase().includes(term))
      .sort((a, b) => Number(isDefaultGroup(b.group)) - Number(isDefaultGroup(a.group)) || a.group.localeCompare(b.group) || a.value.localeCompare(b.value, undefined, { numeric: true }));
  }, [rows, tab, search]);

  const groupOptions = useMemo(() => {
    const names = [...new Set([DEFAULT_GROUP, ...rows.filter((row) => tabOf(row.aclType) === tab).map((row) => row.group)])];
    names.sort((a, b) => Number(isDefaultGroup(b)) - Number(isDefaultGroup(a)) || a.localeCompare(b));
    return names.map((name) => ({ value: name, label: name }));
  }, [rows, tab]);

  const replaceRow = (updated) => setRows((current) => current.map((row) => (row._id === updated._id ? updated : row)));

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const openEdit = (row) => {
    setEditingId(row._id);
    setForm({ group: row.group, value: row.value, label: row.label, permissions: [...(row.permissions || [])], isActive: row.isActive });
  };

  const closeForm = () => {
    setForm(null);
    setEditingId(null);
    setNewGroup(null);
  };

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const togglePlatform = (platform) =>
    setField("permissions", form.permissions.includes(platform) ? form.permissions.filter((item) => item !== platform) : [...form.permissions, platform]);

  const createGroup = (input) => {
    const name = groupName(input);
    if (!name) {
      alert.show("A group name must start with a letter.", { type: "error" });
      return;
    }
    const existing = groupOptions.find((option) => option.value.toLowerCase() === name.toLowerCase());
    setField("group", existing ? existing.value : name);
    setNewGroup(null);
  };

  const deleteGroup = async (name) => {
    const { aclType } = TABS[tab];
    const inGroup = rows.filter((row) => row.aclType === aclType && row.group === name).length;
    const result = await Swal.fire({
      title: "Delete group?",
      text: `${name} and its ${inGroup} code${inGroup === 1 ? "" : "s"} will be deleted, and it will be taken away from every company.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete",
    });
    if (!result.isConfirmed) return;

    try {
      await window.axios.delete(`/acl/group/${aclType}/${name}`);
      setRows((current) => current.filter((row) => !(row.aclType === aclType && row.group === name)));
      setForm((current) => (current && current.group === name ? { ...current, group: DEFAULT_GROUP } : current));
      dispatch(refreshAclCodes());
      alert.show("Group deleted.");
    } catch (error) {
      alert.show(errorText(error), { type: "error" });
    }
  };

  const save = async () => {
    if (saving) return;

    if (!form.value.trim() || !form.label.trim()) {
      alert.show("Code and label are required.", { type: "error" });
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const { label, permissions, isActive } = form;
        const response = await window.axios.put(`/acl/${editingId}`, { label, permissions, isActive });
        replaceRow(response.data);
        alert.show("Updated.");
      } else {
        const { group, value, label, permissions, isActive } = form;
        const response = await window.axios.post("/acl", { aclType: TABS[tab].aclType, group, value: value.trim(), label, permissions, isActive });
        setRows((current) => [...current, response.data]);
        alert.show("Added.");
      }
      dispatch(refreshAclCodes());
      closeForm();
    } catch (error) {
      alert.show(errorText(error), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  // Records may still hold the code. They have to be moved to another code before it can be deleted.
  const remove = async (row) => {
    let usage;
    try {
      usage = (await window.axios.get(`/acl/${row._id}/usage`)).data;
    } catch (error) {
      alert.show(errorText(error), { type: "error" });
      return;
    }

    const noun = TABS[tab].title.toLowerCase();
    let replaceWith = "";

    if (usage.total) {
      const usedOn = usage.platforms.map((item) => item.platform);
      const choices = rows
        .filter((item) => item.aclType === row.aclType && isDefaultGroup(item.group) && item.isActive && item._id !== row._id)
        .filter((item) => usedOn.every((platform) => (item.permissions || []).includes(platform)))
        .sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true }));

      if (!choices.length) {
        alert.show(`${usage.total} record(s) use this ${noun}, and no default ${noun} is available on ${usedOn.join(", ")} to move them to.`, { type: "error" });
        return;
      }

      const result = await Swal.fire({
        title: `This ${noun} is in use`,
        html: `<b>${usage.total}</b> record${usage.total === 1 ? "" : "s"} use <b>${row.value} - ${row.label}</b>:<br>${usage.platforms
          .map((item) => `${item.platform}: ${item.count}`)
          .join("<br>")}<br><br>Choose the ${noun} they should change to. Then the ${noun} is deleted.`,
        icon: "warning",
        input: "select",
        inputOptions: Object.fromEntries(choices.map((item) => [item.value, `${item.value} - ${item.label}`])),
        inputPlaceholder: `Select a ${noun}`,
        inputValidator: (value) => (value ? undefined : `Select the ${noun} to move the records to.`),
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Move records and delete",
      });
      if (!result.isConfirmed) return;
      replaceWith = result.value;
    } else {
      const result = await Swal.fire({
        title: "Delete?",
        text: `${row.value} - ${row.label} will be removed from ${row.group}. No record uses it.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete",
      });
      if (!result.isConfirmed) return;
    }

    try {
      const response = await window.axios.delete(`/acl/${row._id}`, { params: replaceWith ? { replaceWith } : {} });
      setRows((current) => current.filter((item) => item._id !== row._id));
      dispatch(refreshAclCodes());
      alert.show(response.data.moved ? `Deleted. ${response.data.moved} record(s) moved.` : "Deleted.");
    } catch (error) {
      alert.show(errorText(error), { type: "error" });
    }
  };

  const renderPlatforms = (permissions) => {
    if (!permissions.length) return <span className="text-muted">None (hidden everywhere)</span>;
    if (permissions.length === PLATFORMS.length) return <Badge variant="info">All platforms</Badge>;

    const names = [...permissions].sort((a, b) => platformOrder(a) - platformOrder(b)).map((permission) => PLATFORMS.find((platform) => platform.value === permission)?.label || permission);
    names.sort((a, b) => Number(b === MAIN_PLATFORM) - Number(a === MAIN_PLATFORM));

    const shown = names.length > 2 ? names.slice(0, 1) : names;
    const hidden = names.slice(shown.length);

    return (
      <>
        {shown.map((name) => (
          <Badge key={name} variant="secondary" className="mr-1">
            {name}
          </Badge>
        ))}
        {hidden.length > 0 && (
          <Badge variant="secondary" style={{ backgroundColor: "#fff", color: "#495057", border: "1px solid #adb5bd" }} title={names.join(", ")}>
            +{hidden.length} others
          </Badge>
        )}
      </>
    );
  };

  return (
    <div className="row">
      <AdminSideNav />

      <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
        <TopNav />

        <div className="main-content-container container-fluid px-4">
          <div className="page-header row no-gutters py-4">
            <div className="col-12 col-sm-4 text-sm-left mb-0">
              <span className="text-uppercase page-subtitle">Status &amp; Reason</span>
            </div>
            <div className="col-12 col-sm-8 d-flex justify-content-sm-end">
              <Form.Control
                type="search"
                placeholder="Search code, label or group"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ maxWidth: 260 }}
                className="mr-2"
              />
              <Button onClick={openAdd}>Add {TABS[tab].title}</Button>
            </div>
          </div>

          <Nav variant="tabs" activeKey={tab} onSelect={(key) => setTab(key)} className="mb-3">
            {Object.keys(TABS).map((key) => (
              <Nav.Item key={key}>
                <Nav.Link eventKey={key}>
                  {TABS[key].title} ({countFor(key)})
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          <div className="card card-small mb-4">
            <div className="card-body p-0 pb-3">
              <Table className="table mb-0">
                <thead className="bg-light uppercase-th">
                  <tr>
                    <th className="border-0">Code</th>
                    <th className="border-0">Label</th>
                    <th className="border-0">Group</th>
                    <th className="border-0">Platforms</th>
                    <th className="border-0">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={5}>Loading...</td>
                    </tr>
                  )}
                  {!loading && !visibleRows.length && (
                    <tr>
                      <td colSpan={5}>Nothing found.</td>
                    </tr>
                  )}
                  {visibleRows.map((row) => (
                    <tr key={row._id}>
                      <td>{row.value}</td>
                      <td>
                        {row.label}
                        {!row.isActive && (
                          <Badge variant="warning" className="ml-2">
                            Inactive
                          </Badge>
                        )}
                      </td>
                      <td>{row.group}</td>
                      <td>{renderPlatforms(row.permissions || [])}</td>
                      <td>
                        <Button size="sm" variant="outline-primary" className="mr-2" onClick={() => openEdit(row)}>
                          Edit
                        </Button>
                        {!isDefaultGroup(row.group) && (
                          <Button size="sm" variant="outline-danger" onClick={() => remove(row)}>
                            Delete
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
        <Footer />
      </main>

      <Modal show={!!form} onHide={closeForm} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingId ? "Edit" : "Add"} {TABS[tab].title.toLowerCase()}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {form && (
            <Form>
              <Form.Group>
                <Form.Label>Group</Form.Label>
                {newGroup !== null && (
                  <div className="d-flex mb-2">
                    <Form.Control
                      autoFocus
                      placeholder="New group name"
                      value={newGroup}
                      onChange={(event) => setNewGroup(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          createGroup(newGroup);
                        }
                      }}
                    />
                    <Button className="ml-2" onClick={() => createGroup(newGroup)}>
                      Add
                    </Button>
                    <Button variant="secondary" className="ml-2" onClick={() => setNewGroup(null)}>
                      Cancel
                    </Button>
                  </div>
                )}
                <CreatableSelect
                  inputId="acl-type"
                  isDisabled={!!editingId}
                  options={groupOptions}
                  value={{ value: form.group, label: form.group }}
                  onChange={(option) => setField("group", option.value)}
                  onCreateOption={createGroup}
                  onDeleteGroup={deleteGroup}
                  onNewGroup={() => setNewGroup("")}
                  components={{ Option: GroupOption, MenuList: GroupMenuList }}
                  isValidNewOption={(input, current, options) => {
                    const name = groupName(input);
                    return !!name && !options.some((option) => option.value.toLowerCase() === name.toLowerCase());
                  }}
                  formatCreateLabel={(input) => `Create group "${groupName(input)}"`}
                  placeholder="Select or type a new group..."
                />
                <Form.Text className="text-muted">
                  Open the list and pick + Create new group to make one. It is saved with the code. Use &times; on a group in the list to delete it.
                </Form.Text>
              </Form.Group>
              <Form.Group>
                <Form.Label>Code</Form.Label>
                <Form.Control value={form.value} disabled={!!editingId} onChange={(event) => setField("value", event.target.value)} />
                <Form.Text className="text-muted">Stored on records. It cannot be changed after saving.</Form.Text>
              </Form.Group>
              <Form.Group>
                <Form.Label>Label</Form.Label>
                <Form.Control value={form.label} onChange={(event) => setField("label", event.target.value)} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Platforms</Form.Label>
                <div>
                  {PLATFORMS.map((platform) => (
                    <Form.Check
                      inline
                      key={platform.value}
                      type="checkbox"
                      id={`acl-platform-${platform.value}`}
                      label={platform.label}
                      checked={form.permissions.includes(platform.value)}
                      onChange={() => togglePlatform(platform.value)}
                    />
                  ))}
                </div>
              </Form.Group>
              <Form.Check
                type="checkbox"
                id="acl-form-active"
                label="Active"
                checked={form.isActive}
                onChange={(event) => setField("isActive", event.target.checked)}
              />
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeForm}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

const mapStateToProps = (state) => ({ login: state.login, response: state.response });

export default withAlert()(connect(mapStateToProps)(authorizeAdmin(requireAuth(ManageAcl))));
